<template>
  <!-- Kebele Mapping Modal -->
  <div v-if="show" class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5)">
    <div class="modal-dialog modal-xl modal-dialog-centered" style="max-height: 90vh; overflow-y: auto;">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Kebele Mapping Required</h5>
          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>
        <div class="modal-body" style="max-height: 60vh; overflow-y: auto;">
          <div class="alert alert-warning" role="alert">
            <h6 class="alert-heading">Some kebeles in your CSV file were not found in the system</h6>
            <p>Please map the following kebeles to existing ones in the database, or skip the problematic rows.</p>
          </div>

          <div v-for="(mapping, index) in localMappings" :key="index" class="card mb-3">
            <div class="card-header">
              <h6 class="mb-0">CSV Kebele: <strong>{{ mapping.inputName }}</strong></h6>
              <small class="text-muted">{{ mapping.affectedRows }} rows affected</small>
            </div>
            <div class="card-body">
              <div v-if="mapping.suggestions && mapping.suggestions.length > 0 && !mapping.applied && !mapping.skipped">
                <label class="form-label">Select matching kebele:</label>
                <div class="row">
                  <div class="col-md-8">
                    <select v-model="mapping.selectedKebele" class="form-select">
                      <option :value="null">-- Select a kebele --</option>
                      <option v-for="suggestion in mapping.suggestions"
                              :key="suggestion.kebele_id"
                              :value="suggestion">
                        {{ suggestion.kebele_name }} ({{ suggestion.woreda_name }}, {{ suggestion.zone_name }})
                      </option>
                    </select>
                  </div>
                  <div class="col-md-4">
                    <button @click="searchKebeles(mapping)" class="btn btn-outline-primary">
                      <i class="bx bx-search me-1"></i>Search More
                    </button>
                  </div>
                </div>
              </div>

              <div v-else-if="!mapping.applied && !mapping.skipped">
                <p class="text-muted">No similar kebeles found. You can:</p>
                <div class="row">
                  <div class="col-md-8">
                    <input v-model="mapping.searchTerm"
                           @keyup.enter="searchKebeles(mapping)"
                           type="text"
                           class="form-control"
                           placeholder="Search for kebele...">
                  </div>
                  <div class="col-md-4">
                    <button @click="searchKebeles(mapping)" class="btn btn-primary">
                      <i class="bx bx-search me-1"></i>Search
                    </button>
                  </div>
                </div>
              </div>

              <!-- Search Results -->
              <div v-if="mapping.searchResults && mapping.searchResults.length > 0 && !mapping.applied && !mapping.skipped" class="mt-3">
                <label class="form-label">Search Results:</label>
                <div class="table-responsive" style="max-height: 200px;">
                  <table class="table table-sm table-bordered table-hover">
                    <thead>
                    <tr>
                      <th width="50">Select</th>
                      <th>Kebele Name</th>
                      <th>Woreda</th>
                      <th>Zone</th>
                      <th>Region</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="kebele in mapping.searchResults"
                        :key="kebele.kebele_id"
                        @click="selectKebele(mapping, kebele)"
                        style="cursor: pointer;">
                      <td>
                        <input type="radio"
                               :name="'kebele_' + index"
                               :checked="mapping.selectedKebele && mapping.selectedKebele.kebele_id === kebele.kebele_id"
                               @click.stop="selectKebele(mapping, kebele)"
                               class="form-check-input">
                      </td>
                      <td>{{ kebele.kebele_name }}</td>
                      <td>{{ kebele.woreda_name }}</td>
                      <td>{{ kebele.zone_name }}</td>
                      <td>{{ kebele.region_name }}</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- No results message -->
              <div v-if="mapping.searchAttempted && mapping.searchResults && mapping.searchResults.length === 0 && !mapping.applied && !mapping.skipped" class="mt-3">
                <div class="alert alert-info">
                  <i class="bx bx-info-circle me-1"></i>
                  No kebeles found matching "{{ mapping.searchTerm }}". Try a different search term.
                </div>
              </div>

              <!-- Actions -->
              <div v-if="!mapping.applied && !mapping.skipped" class="mt-3 d-flex gap-2">
                <button v-if="mapping.selectedKebele"
                        @click="applyMapping(mapping)"
                        class="btn btn-success btn-sm">
                  <i class="bx bx-check me-1"></i>Apply Mapping
                </button>
                <button @click="skipRows(mapping)"
                        class="btn btn-warning btn-sm">
                  <i class="bx bx-x me-1"></i>Skip These Rows
                </button>
              </div>

              <!-- Applied mapping display -->
              <div v-if="mapping.applied" class="mt-2">
                <div class="alert alert-success p-2 mb-0">
                  <i class="bx bx-check-circle me-1"></i>
                  Mapped to: <strong>{{ mapping.appliedKebele.kebele_name }}</strong>
                  ({{ mapping.appliedKebele.woreda_name }}, {{ mapping.appliedKebele.zone_name }})
                  <button @click="undoMapping(mapping)" class="btn btn-sm btn-outline-secondary float-end">
                    <i class="bx bx-undo me-1"></i>Undo
                  </button>
                </div>
              </div>

              <!-- Skipped display -->
              <div v-if="mapping.skipped" class="mt-2">
                <div class="alert alert-warning p-2 mb-0">
                  <i class="bx bx-info-circle me-1"></i>
                  {{ mapping.affectedRows }} rows will be skipped
                  <button @click="undoSkip(mapping)" class="btn btn-sm btn-outline-secondary float-end">
                    <i class="bx bx-undo me-1"></i>Undo
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <div class="d-flex justify-content-between w-100 align-items-center">
            <div>
              <small class="text-muted">
                <strong>{{ completedMappings }} of {{ localMappings.length }}</strong> mappings completed
              </small>
              <span v-if="!allMappingsCompleted" class="badge bg-warning text-dark ms-2">
                {{ localMappings.length - completedMappings }} remaining
              </span>
              <span v-else class="badge bg-success ms-2">
                <i class="bx bx-check me-1"></i>All mappings completed
              </span>
            </div>
            <div>
              <button @click="closeModal" class="btn btn-secondary me-2">Cancel</button>
              <button @click="proceedWithMappings"
                      :disabled="!allMappingsCompleted"
                      class="btn btn-primary"
                      :class="{ 'btn-success': allMappingsCompleted }">
                <i class="bx bx-upload me-1"></i>Proceed with Upload
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { ref, computed, watch } from 'vue';
import { executeJson, authUrl } from "@/api";

export default {
  name: "KebeleMapping",

  props: {
    show: {
      type: Boolean,
      default: false
    },
    mappings: {
      type: Array,
      default: () => []
    }
  },

  emits: [
    'close',
    'apply-mapping',
    'skip-rows',
    'proceed',
    'show-alert'
  ],

  setup(props, { emit }) {
    // Create a local reactive copy of mappings
    const localMappings = ref([]);

    // Watch for changes in props.mappings and update local copy
    watch(() => props.mappings, (newMappings) => {
      localMappings.value = newMappings.map(m => ({
        ...m,
        selectedKebele: m.selectedKebele || null,
        searchResults: m.searchResults || [],
        searchAttempted: m.searchAttempted || false,
        searchTerm: m.searchTerm || m.inputName || '',
        applied: m.applied || false,
        skipped: m.skipped || false,
        appliedKebele: m.appliedKebele || null
      }));
    }, { immediate: true, deep: true });

    const completedMappings = computed(() => {
      return localMappings.value.filter(m => m.applied || m.skipped).length;
    });

    const allMappingsCompleted = computed(() => {
      const completed = localMappings.value.length > 0 &&
          localMappings.value.every(m => m.applied || m.skipped);
      console.log('All mappings completed:', completed, completedMappings.value, '/', localMappings.value.length);
      return completed;
    });

    const closeModal = () => {
      emit('close');
    };

    const selectKebele = (mapping, kebele) => {
      mapping.selectedKebele = kebele;
    };

    const searchKebeles = async (mapping) => {
      if (!mapping.searchTerm && mapping.inputName) {
        mapping.searchTerm = mapping.inputName;
      }

      if (!mapping.searchTerm || !mapping.searchTerm.trim()) {
        emit('show-alert', 'failed', 'Please enter a search term');
        return;
      }

      try {
        const data = {
          function: "getAvailableKebeles",
          search: mapping.searchTerm
        };

        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          mapping.searchResults = res.data.data.kebeles || [];
          mapping.searchAttempted = true;

          if (mapping.searchResults.length === 0) {
            emit('show-alert', 'info', 'No kebeles found matching your search');
          }
        } else {
          emit('show-alert', 'failed', 'Error searching kebeles: ' + res.data.message);
        }
      } catch (error) {
        console.error('Search error:', error);
        emit('show-alert', 'failed', 'Error searching kebeles: ' + error.message);
      }
    };

    const applyMapping = (mapping) => {
      if (!mapping.selectedKebele) {
        emit('show-alert', 'failed', 'Please select a kebele to map to');
        return;
      }

      mapping.applied = true;
      mapping.appliedKebele = mapping.selectedKebele;
      mapping.skipped = false;

      emit('apply-mapping', {
        inputName: mapping.inputName,
        selectedKebele: mapping.selectedKebele
      });

      emit('show-alert', 'success', `Mapped "${mapping.inputName}" to "${mapping.selectedKebele.kebele_name}"`);
    };

    const skipRows = (mapping) => {
      mapping.skipped = true;
      mapping.applied = false;

      emit('skip-rows', {
        inputName: mapping.inputName,
        affectedRows: mapping.affectedRows
      });

      emit('show-alert', 'info', `Skipped ${mapping.affectedRows} rows with kebele "${mapping.inputName}"`);
    };

    const undoMapping = (mapping) => {
      mapping.applied = false;
      mapping.appliedKebele = null;
      mapping.selectedKebele = null;

      emit('show-alert', 'info', 'Mapping undone');
    };

    const undoSkip = (mapping) => {
      mapping.skipped = false;

      emit('show-alert', 'info', 'Skip undone');
    };

    const proceedWithMappings = () => {
      if (!allMappingsCompleted.value) {
        emit('show-alert', 'warning', 'Please complete all mappings before proceeding');
        return;
      }

      console.log('Proceeding with mappings...');
      emit('proceed');
    };

    return {
      localMappings,
      completedMappings,
      allMappingsCompleted,
      closeModal,
      selectKebele,
      searchKebeles,
      applyMapping,
      skipRows,
      undoMapping,
      undoSkip,
      proceedWithMappings
    };
  }
};
</script>

<style scoped>
.modal.show {
  display: block !important;
}

.modal-dialog {
  margin: 1.75rem auto;
}

.table-responsive {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
}

.table-hover tbody tr:hover {
  background-color: #f8f9fa;
}

.form-check-input {
  cursor: pointer;
}

.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  transition: box-shadow 0.3s ease;
}

.card:hover {
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.alert {
  border: 1px solid transparent;
  border-radius: 0.375rem;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.gap-2 {
  gap: 0.5rem;
}

.badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

.btn-success.btn-primary {
  background-color: #28a745;
  border-color: #28a745;
}

.btn-success.btn-primary:hover {
  background-color: #218838;
  border-color: #1e7e34;
}

.float-end {
  float: right;
}
</style>